import { useState, useMemo, useEffect, useCallback } from 'react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import SettingsToggle from '../components/SettingsToggle';
import Loader from '../components/Loader';
import DataPrivacyCard from '../components/DataPrivacyCard';
import SearchableSelect from '../components/SearchableSelect';
import { AI_TOGGLES, NOTIFICATION_TOGGLES, INTEGRATIONS } from '../data/settingsData';
import { formatDateTime } from '../utils/date';

const TABS = [
  { key: 'general', label: 'General', icon: 'fa-sliders' },
  { key: 'ai', label: 'AI Agent', icon: 'fa-robot' },
  { key: 'notifications', label: 'Notifications', icon: 'fa-bell' },
  { key: 'integrations', label: 'Integrations', icon: 'fa-plug' },
  { key: 'rbac', label: 'User Access', icon: 'fa-shield-halved' },
];

const REAL_MODULES = [
  { key: 'course_level_master', label: 'Course Level Master' },
  { key: 'course_master', label: 'Course Master' },
  { key: 'intake_master', label: 'Intake Master' },
  { key: 'component_master', label: 'Component Master' },
  { key: 'module_master', label: 'Module Master' },
  { key: 'timetable_master', label: 'Timetable Scheduler' },
];

const ROLE_META = {
  super_admin: { label: 'Super Admin', icon: 'fa-shield-halved', col: '#7C3AED', bg: '#EDE9FE' },
  admin: { label: 'Admin', icon: 'fa-user-tie', col: '#0B3552', bg: '#EFF6FF' },
  staff: { label: 'Staff', icon: 'fa-calendar-pen', col: '#006B5C', bg: '#E0FAF6' },
  trainer: { label: 'Trainer', icon: 'fa-chalkboard-user', col: '#B06A00', bg: '#FEF3DC' },
  student: { label: 'Student', icon: 'fa-user-graduate', col: '#C04010', bg: '#FDE8DF' },
};
const ROLE_ORDER = ['super_admin', 'admin', 'staff', 'trainer', 'student'];

function initials(name = '') {
  return name.split(' ').slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
}

// OR-aggregates every user of a role for one module: full access if any of
// them can create/update/delete, view-only if any can at least view, else none.
function aggregateRolePerm(usersOfRole, moduleKey) {
  let anyFull = false;
  let anyView = false;
  for (const u of usersOfRole) {
    const p = u.permissions?.[moduleKey];
    if (!p) continue;
    if (p.create || p.update || p.delete) anyFull = true;
    else if (p.view) anyView = true;
  }
  if (anyFull) return 1;
  if (anyView) return 2;
  return 0;
}

export default function SettingsPage() {
  const { show } = useToast();
  const { logout, user: currentUser } = useAuth();
  const [tab, setTab] = useState('general');
  const [roleFilter, setRoleFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const loadUsers = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get('/users')
      .then((res) => { setUsers(res.data.data); setLoaded(true); })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'rbac' && !loaded && !loading) loadUsers();
  }, [tab, loaded, loading, loadUsers]);

  const filteredUsers = useMemo(() => (roleFilter ? users.filter((u) => u.role === roleFilter) : users), [users, roleFilter]);

  const rolesPresent = useMemo(() => {
    const present = new Set(users.map((u) => u.role));
    return ROLE_ORDER.filter((r) => present.has(r));
  }, [users]);

  const handleRoleChange = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}/role`, { role });
      show(`Role updated for ${u.name}`, 'success');
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, role } : x)));
    } catch (err) {
      show(err.response?.data?.message || 'Failed to update role', 'error');
    }
  };

  return (
    <div>
      <div className="sh-ey">SYSTEM SETTINGS</div>
      <div className="sh-ti">Configuration</div>
      <div className="sh-de">Manage academic terms, AI Agent settings, notification preferences, system integrations, and role-based user access control.</div>

      <div className="tabs" style={{ marginBottom: '1.5rem' }}>
        {TABS.map((t) => (
          <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            <i className={`fa-solid ${t.icon}`} style={{ marginRight: '.38rem' }} />{t.label}
          </div>
        ))}
      </div>

      {tab === 'general' && (
        <div className="stab">
          <div className="g2" style={{ alignItems: 'start' }}>
            <div className="card">
              <div className="ct">Academic Parameters</div>
              <div className="fg"><label className="fl">Active Academic Year</label><select className="finput"><option>AY2026</option><option>AY2025</option></select></div>
              <div className="fg"><label className="fl">Active Semester</label><select className="finput"><option>Semester 1 (Oct 2026 – Feb 2027)</option><option>Semester 2</option></select></div>
              <div className="fg"><label className="fl">Max Teaching Load (hrs/term)</label><input className="finput" type="number" defaultValue={20} /></div>
              <div className="fg"><label className="fl">Room Utilisation Target (%)</label><input className="finput" type="number" defaultValue={75} /></div>
              <div className="fg"><label className="fl">STB Licence Warning (days before expiry)</label><input className="finput" type="number" defaultValue={90} /></div>
              <button className="btn btn-primary btn-sm" onClick={() => show('Settings saved', 'success')}><i className="fa-solid fa-floppy-disk" /> Save Parameters</button>
            </div>
            <div className="card" style={{ borderColor: '#FECACA' }}>
              <div style={{ fontSize: '.72rem', fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '.12em', color: 'var(--red)', marginBottom: '.72rem' }}>DANGER ZONE</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)', marginBottom: '.88rem' }}>Archiving the term locks all plans and exports STB records.</div>
              <div style={{ display: 'flex', gap: '.6rem' }}>
                <button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm('Archive AY2026 S1?')) show('Term archived and STB export generated', 'info'); }}><i className="fa-solid fa-archive" /> Archive Term</button>
                <button className="btn btn-danger btn-sm" onClick={logout}><i className="fa-solid fa-right-from-bracket" /> Sign Out</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'ai' && (
        <div className="stab">
          {['admin', 'super_admin'].includes(currentUser?.role) && <DataPrivacyCard />}
          <div className="card">
            <div className="ct">AI Agent Configuration</div>
            {AI_TOGGLES.map((s) => <SettingsToggle key={s.l} label={s.l} desc={s.d} defaultOn={s.on} />)}
            <div style={{ marginTop: '.85rem', background: 'rgba(29,78,216,.08)', borderRadius: 8, padding: '.85rem', border: '1px solid rgba(29,78,216,.15)' }}>
              <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: '#1D4ED8', marginBottom: '.2rem' }}><i className="fa-solid fa-robot" /> AI Model</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-s)' }}>The Scheduling Assistant uses an external AI provider, with Sensitive Data Protection applied to every request unless an administrator turns it off.</div>
            </div>
          </div>
        </div>
      )}

      {tab === 'notifications' && (
        <div className="stab">
          <div className="card">
            <div className="ct">Notification Preferences</div>
            {NOTIFICATION_TOGGLES.map((s) => <SettingsToggle key={s.l} label={s.l} desc={s.d} defaultOn={s.on} />)}
          </div>
        </div>
      )}

      {tab === 'integrations' && (
        <div className="stab">
          <div className="card">
            <div className="ct">System Integrations</div>
            {INTEGRATIONS.map((i) => (
              <div key={i.n} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.72rem', background: 'var(--cloud)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: '.48rem' }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: i.c, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--surface)', fontSize: '.88rem', flexShrink: 0 }}><i className={`fa-solid ${i.i}`} /></div>
                <div style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)' }}>{i.n}</div>
                <span className={`pill ${i.s === 'Connected' ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${i.s === 'Connected' ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {i.s}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => show('Integration settings coming soon', 'info')}><i className="fa-solid fa-gear" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'rbac' && (
        <div className="stab">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '.75rem' }}>
            <div>
              <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--ink)', fontFamily: 'var(--font-serif)' }}>Role-Based Access Control</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)', marginTop: '.18rem' }}>Live from the backend — one card per role actually present in the system, aggregated from each user's real permissions.</div>
            </div>
          </div>

          {loading && <Loader label="Loading users…" />}
          {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

          {!loading && !error && (
            <>
              <div className="g2" style={{ marginBottom: '1.5rem', alignItems: 'start' }}>
                {rolesPresent.map((role) => {
                  const meta = ROLE_META[role];
                  const usersOfRole = users.filter((u) => u.role === role);
                  return (
                    <div key={role} className="rbac-role-card">
                      <div className="rbac-role-hd" style={{ background: meta.bg }}>
                        <div className="rbac-role-ico" style={{ background: meta.col, color: '#fff' }}><i className={`fa-solid ${meta.icon}`} /></div>
                        <div style={{ flex: 1 }}>
                          <div className="rbac-role-name">{meta.label}</div>
                          <div className="rbac-role-count">{usersOfRole.length} user{usersOfRole.length !== 1 ? 's' : ''}</div>
                        </div>
                        <span className="rbac-badge" style={{ background: `${meta.col}22`, color: meta.col, border: `1px solid ${meta.col}44` }}>{usersOfRole.length} user{usersOfRole.length !== 1 ? 's' : ''}</span>
                      </div>
                      {role === 'super_admin' ? (
                        <div style={{ padding: '.9rem 1.1rem', fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>Bypasses the permission matrix entirely — always full access.</div>
                      ) : REAL_MODULES.map((m) => {
                        const v = aggregateRolePerm(usersOfRole, m.key);
                        const cls = v === 1 ? 'rbac-yes' : v === 2 ? 'rbac-partial' : 'rbac-no';
                        const ico = v === 1 ? 'fa-check' : v === 2 ? 'fa-eye' : 'fa-xmark';
                        const label = v === 1 ? 'Full access' : v === 2 ? 'View only' : 'No access';
                        return (
                          <div key={m.key} className="rbac-perm-row">
                            <span className="rbac-perm-label">{m.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '.55rem' }}>
                              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>{label}</span>
                              <div className={`rbac-check ${cls}`}><i className={`fa-solid ${ico}`} /></div>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ padding: '.72rem 1.1rem', borderTop: '1px solid var(--border-soft)' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => show('Editing per-user permissions is on the Course Level/Course/Intake/Component/Module Master pages via the API — a dedicated editor here is coming soon', 'info')}><i className="fa-solid fa-pencil" /> Edit Permissions</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.6rem' }}>
                  <div className="ct" style={{ margin: 0 }}>User Access Management</div>
                  <SearchableSelect
                    className="finput" style={{ width: 195 }} value={roleFilter} onChange={setRoleFilter}
                    placeholder="All Roles"
                    options={[{ value: '', label: 'All Roles' }, ...ROLE_ORDER.map((r) => ({ value: r, label: ROLE_META[r].label }))]}
                  />
                </div>
                {!filteredUsers.length ? (
                  <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--slate)' }}>No users found.</div>
                ) : (
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th></tr></thead>
                      <tbody>
                        {filteredUsers.map((u) => (
                          <tr key={u.id}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '.68rem' }}>
                                <div className="rbac-user-ava" style={{ background: 'linear-gradient(135deg,var(--gold-bg),var(--coral-bg))' }}>{initials(u.name)}</div>
                                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)' }}>{u.name}</div>
                              </div>
                            </td>
                            <td><span style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>{u.email}</span></td>
                            <td>
                              <SearchableSelect
                                className="rbac-select"
                                value={u.role}
                                disabled={u.id === currentUser?.id}
                                onChange={(val) => handleRoleChange(u, val)}
                                options={ROLE_ORDER.map((r) => ({ value: r, label: ROLE_META[r].label }))}
                              />
                            </td>
                            <td><span className={`pill ${u.is_active ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${u.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {u.is_active ? 'Active' : 'Inactive'}</span></td>
                            <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--slate)' }}>{u.last_login ? formatDateTime(u.last_login) : '—'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="card">
            <div className="ct">Access Level Legend</div>
            <div className="g3">
              <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.72rem', background: 'var(--green-dim)', borderRadius: 8, border: '1px solid #A7F3D0' }}>
                <div className="rbac-check rbac-yes" style={{ width: 30, height: 30, fontSize: '.85rem' }}><i className="fa-solid fa-check" /></div>
                <div><div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--green)' }}>Full Access</div><div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-s)', marginTop: '.1rem' }}>View, create, edit, delete and manage</div></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.72rem', background: 'var(--gold-dim)', borderRadius: 8, border: '1px solid #FDE68A' }}>
                <div className="rbac-check rbac-partial" style={{ width: 30, height: 30, fontSize: '.85rem' }}><i className="fa-solid fa-eye" /></div>
                <div><div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--gold)' }}>View Only</div><div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-s)', marginTop: '.1rem' }}>Read-only — no create or edit</div></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.72rem', background: 'var(--cloud)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div className="rbac-check rbac-no" style={{ width: 30, height: 30, fontSize: '.85rem' }}><i className="fa-solid fa-xmark" /></div>
                <div><div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--slate)' }}>No Access</div><div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-s)', marginTop: '.1rem' }}>Module hidden from this role's navigation</div></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
